import { withTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useParams } from "react-router-dom";
import CustomerReservationsClient from "../../../clients/CustomerReservationsClient";
import SubNav from "../../../shared/subnav/SubNav";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import fordStyles from "../../../styles/common/CustomerHandlingStyles.module.scss";
import lincolnStyles from "../../../styles/common/CustomerHandlingStyles-Lincoln.module.scss";
import { Box, Breadcrumbs, Skeleton, Typography } from "@mui/material";
import SearchOrdersResults from "../tabs/SearchOrdersResults";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import MotionClose from "../../../shared/Icons/MotionClose";
import { getMarketBrandPath } from "../../../utils/marketBrandUtils";

const SearchResultsWrapper = (props) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const { keyword } = useParams();

  useEffect(() => {
    setSearchKeyword(keyword);
    searchOrder(keyword);
    // Only re-run the search when the URL keyword changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const searchOrder = (keyword) => {
    setLoading(true);
    let orders = [];

    let dealers = "";
    if (props.user.market === "CAN" && props.user.showAll) {
      dealers = props.user.getCommonIds();
    } else {
      dealers = props.user.commonDealerId;
    }

    const checkLastName = (keyword) => {
      const trimmedKeyword = keyword.trim();
      return (
        !trimmedKeyword.includes(" ") &&
        !trimmedKeyword.includes("@") &&
        !/\d/.test(trimmedKeyword)
      );
    };

    const isLastNameSearch = checkLastName(keyword);

    CustomerReservationsClient.searchCustomerReservations(
      keyword.trim(),
      {},
      props.user.lang,
      dealers,
      null,
      null,
      null,
      isLastNameSearch
    )
      .then((response) => {
        orders = response;
        if (orders !== null) {
          setOrders(orders);
        }
      })
      .catch(() => {
        orders = [];
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getNoContent = (tab) => {
    const styles = IS_FORD_BRAND(props.user.brand) ? fordStyles : lincolnStyles;

    return (
      <div className={styles.noResultsDiv}>
        <div className={styles.noResultsFound}>
          {props.t("CustomerOrders." + tab)}
        </div>
      </div>
    );
  };

  const styles = IS_FORD_BRAND(props.user.brand) ? fordStyles : lincolnStyles;
  const isLincoln = props.user.brand === "L";
  const isDealer = props.user.userType === "na_dealer";
  const toAppPath = (path) =>
    isDealer
      ? getMarketBrandPath(props.user.market, props.user.brand, path)
      : path;
  const customerHandlingPath = toAppPath("/customer-handling");

  return (
    <Box id="dashboard" className="pageWrapper">
      <SubNav
        id="subNav"
        dashboard={true}
        title={props.t("GlobalNavBar.dashboard")}
        {...props}
        getNoContent={getNoContent}
        searchKeyword={searchKeyword}
        isDealer={true}
      />
      <Box
        component="section"
        className={styles.resultsWrapper}
        sx={{ py: 4, px: { xs: 4, sm: 4, md: 4, lg: 6 } }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={
            isLincoln
              ? {
                  "&, & p, & a": {
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: "#555",
                  },
                }
              : undefined
          }
        >
          <Link to={toAppPath("/dashboard")}>
            {props.t("GlobalNavBar.dashboard")}
          </Link>
          <Link to={customerHandlingPath}>
            {isLincoln && isDealer
              ? "Client Handling"
              : props.t("GlobalNavBar.customerHandling")}
          </Link>
          <Typography variant="body2">
            {props.t("CustomerOrders.searchResults")}
          </Typography>
        </Breadcrumbs>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 6,
            mb: 2,
          }}
        >
          <Typography
            component="h1"
            data-testid="search-results-heading"
            sx={
              isLincoln
                ? {
                    fontSize: 16,
                    fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                    fontWeight: 600,
                    lineHeight: "24px",
                    letterSpacing: 0,
                    overflowWrap: "anywhere",
                  }
                : {
                    typography: "h5",
                    color: "#4d4d4d",
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                  }
            }
          >
            {props.t("CustomerOrders.searchResultsFor", {
              keyword: searchKeyword,
              interpolation: { escapeValue: false },
            })}
          </Typography>
          <EMPButton
            testId="clear-search-button"
            buttonElement={Link}
            link={customerHandlingPath}
            variant="outlined"
            leftIcon={<MotionClose />}
          >
            {props.t("CustomerOrders.clearSearch")}
          </EMPButton>
        </Box>
        {loading ? (
          <Skeleton variant="rectangular" animation="wave" height={100} />
        ) : (
          <div id="Modele">
            <SearchOrdersResults
              {...props}
              orders={orders}
              getNoContent={getNoContent}
              hidePagination={true}
              noContentText="noResults"
            />
          </div>
        )}
      </Box>
    </Box>
  );
};

SearchResultsWrapper.propTypes = {
  user: PropTypes.shape({
    dealerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    market: PropTypes.string,
    showAll: PropTypes.bool,
    getCommonIds: PropTypes.func,
    commonDealerId: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    lang: PropTypes.string,
    brand: PropTypes.string,
    userType: PropTypes.string,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  t: PropTypes.func.isRequired,
  hideOrShow: PropTypes.func.isRequired,
};

export default withTranslation("emp")(SearchResultsWrapper);
